/** Presentation for compact watchlist rank and movement controls. */
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu from "@/components/molecules/DesktopDropdownMenu";
import { GripVerticalIcon, MoreHorizontalIcon } from "lucide-react-native";

import type { WatchlistOrderControlsViewProps } from "./WatchlistOrderControlsContainer";

/**
 * Resolve the handle geometry and sorted-mode visibility.
 * @param tableRow - Use the table grip track.
 * @param compact - Compact pitch geometry.
 * @param paused - Sorting prevents manual movement.
 * @returns Handle classes.
 */
const handleClass = (
  tableRow: boolean | undefined,
  paused: boolean,
  compact?: boolean
) => {
  if (compact)
    return "-order-1 size-6 touch-none items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default";
  if (!tableRow)
    return "size-8 touch-none items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default";
  return `-order-1 h-8 w-6 shrink-0 touch-none items-center justify-center focus-visible:ring-2 focus-visible:ring-outline-default${paused ? " invisible" : ""}`;
};

/**
 * Uses the existing menu geometry at either density.
 * @param compact - Compact pitch row.
 * @returns Semantic utility classes.
 */
const menuClass = (compact?: boolean) =>
  `${compact ? "size-6" : "size-8"} items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default`;

/**
 * Renders rank, drag handle and movement menu.
 * @param root0 - Prepared presentation props.
 * @param root0.compact - Fit the existing grip and menu within a compact pitch row.
 * @param root0.tableRow - Fit controls inside the fixed table content track.
 * @param root0.playerId - Player identifier used by test ids.
 * @param root0.playerName - Player name used by accessible labels.
 * @param root0.rank - Current global watchlist rank.
 * @param root0.canEdit - Whether movement controls are available.
 * @param root0.paused - Whether explicit sorting pauses movement.
 * @param root0.isOpen - Whether the movement menu is open.
 * @param root0.onClose - Closes the movement menu.
 * @param root0.onToggle - Toggles the movement menu.
 * @param root0.items - Prepared movement menu entries.
 * @param root0.nodeRef - Sortable row node ref.
 * @param root0.activatorRef - Sortable handle ref.
 * @param root0.dragBindings - Sortable handle attributes and listeners.
 */
const WatchlistOrderControlsView = ({
  tableRow,
  compact,
  playerId,
  playerName,
  rank,
  canEdit,
  paused,
  isOpen,
  onClose,
  onToggle,
  items,
  nodeRef,
  activatorRef,
  dragBindings,
}: WatchlistOrderControlsViewProps) => (
  <Stack
    ref={nodeRef as never}
    testID={`watchlist-order-controls-${playerId}`}
    direction="horizontal"
    className={
      tableRow ? "h-6 w-16 shrink-0 items-center" : "items-center gap-1"
    }
  >
    <Text
      variant={tableRow ? "title-sm-medium" : "micro"}
      className={
        tableRow
          ? "w-10 text-center text-content-primary"
          : "w-5 text-center text-content-muted"
      }
    >
      {rank}
    </Text>
    {canEdit ? (
      <>
        <Pressable
          {...dragBindings}
          ref={activatorRef as never}
          testID={`watchlist-order-handle-${playerId}`}
          accessibilityRole="button"
          accessibilityLabel={`Move ${playerName}`}
          accessibilityState={{ disabled: paused }}
          disabled={paused}
          className={handleClass(tableRow, paused, compact)}
        >
          <Icon
            as={GripVerticalIcon}
            size={tableRow ? "sm" : "xs"}
            className="text-content-muted"
          />
        </Pressable>
        {tableRow ? null : paused ? (
          <Pressable
            testID={`watchlist-move-menu-${playerId}`}
            accessibilityRole="button"
            accessibilityLabel={`Move ${playerName} menu`}
            accessibilityState={{ disabled: true }}
            disabled
            className="size-8 items-center justify-center rounded-sm"
          >
            <Icon
              as={MoreHorizontalIcon}
              size="xs"
              className="text-content-disabled"
            />
          </Pressable>
        ) : (
          <DesktopDropdownMenu
            isOpen={isOpen}
            onClose={onClose}
            onToggle={onToggle}
            triggerTestID={`watchlist-move-menu-${playerId}`}
            triggerAccessibilityLabel={`Move ${playerName} menu`}
            triggerClassName={menuClass(compact)}
            triggerContent={
              <Icon
                as={MoreHorizontalIcon}
                size="xs"
                className="text-content-muted"
              />
            }
            items={items}
          />
        )}
      </>
    ) : tableRow ? (
      <Stack className="-order-1 w-6" />
    ) : null}
  </Stack>
);

WatchlistOrderControlsView.displayName = "WatchlistOrderControlsView";
export default memo(WatchlistOrderControlsView);
